import React from "react";
import { Dropdown } from "../Dropdown";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import {
  HOUR_OPTIONS,
  TIMEZONE_OPTIONS,
} from "../../../utils/scheduleConstants";
import {
  Field,
  FieldCol,
  FieldLabel,
  FieldRow,
  HelperText,
} from "../shared/scheduleField.styles";

export interface RefreshAtFieldProps {
  /** Hour of day 0–23, or null. */
  hour: number | null;
  /** IANA timezone. */
  timezone: string;
  /** Visible label for the time field. */
  label: string;
  /** Whether to render the timezone selector beside the time selector. */
  showTimezone?: boolean;
  openDropdown: "hour" | "timezone" | null;
  timezoneQuery: string;
  onChangeHour: (hour: number) => void;
  onChangeTimezone: (tz: string) => void;
  onTimezoneQueryChange: (query: string) => void;
  onOpenDropdown: (key: "hour" | "timezone") => void;
  onCloseDropdown: () => void;
  hourError?: string;
  timezoneError?: string;
  showError?: boolean;
  className?: string;
}

/** Hour-of-day dropdown, optionally paired 50/50 with a timezone dropdown. */
export const RefreshAtField: React.FC<RefreshAtFieldProps> = ({
  hour,
  timezone,
  label,
  showTimezone = false,
  openDropdown,
  timezoneQuery,
  onChangeHour,
  onChangeTimezone,
  onTimezoneQueryChange,
  onOpenDropdown,
  onCloseDropdown,
  hourError,
  timezoneError,
  showError = false,
  className,
}) => {
  const visibleHourError = showError && hourError ? hourError : undefined;
  const visibleTzError = showError && timezoneError ? timezoneError : undefined;

  const hourPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "hour",
    onOutsideClick: onCloseDropdown,
  });
  const tzPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "timezone",
    onOutsideClick: onCloseDropdown,
  });

  const hourDropdown = (
    <Dropdown
      value={hour === null ? null : String(hour)}
      options={HOUR_OPTIONS}
      open={openDropdown === "hour"}
      triggerRef={hourPortal.triggerRef}
      menuRef={hourPortal.menuRef}
      menuRect={hourPortal.rect}
      size="md"
      compact
      fullWidth
      placeholder="Pick a time"
      invalid={Boolean(visibleHourError)}
      ariaLabel={label}
      onToggle={() => onOpenDropdown("hour")}
      onSelect={(v) => {
        onChangeHour(Number(v));
        onCloseDropdown();
      }}
    />
  );

  return (
    <Field className={className}>
      <FieldLabel>{label}</FieldLabel>
      {showTimezone ? (
        <FieldRow>
          <FieldCol>{hourDropdown}</FieldCol>
          <FieldCol>
            <Dropdown
              value={timezone}
              options={TIMEZONE_OPTIONS}
              open={openDropdown === "timezone"}
              triggerRef={tzPortal.triggerRef}
              menuRef={tzPortal.menuRef}
              menuRect={tzPortal.rect}
              size="md"
              compact
              fullWidth
              showScrollbar
              searchable
              query={timezoneQuery}
              onQueryChange={onTimezoneQueryChange}
              searchPlaceholder="Search timezone"
              placeholder="Timezone"
              invalid={Boolean(visibleTzError)}
              ariaLabel="Timezone"
              onToggle={() => onOpenDropdown("timezone")}
              onSelect={(v) => {
                onChangeTimezone(v);
                onCloseDropdown();
              }}
            />
          </FieldCol>
        </FieldRow>
      ) : (
        hourDropdown
      )}
      {(visibleHourError ?? visibleTzError) && (
        <HelperText $error>{visibleHourError ?? visibleTzError}</HelperText>
      )}
    </Field>
  );
};

export default RefreshAtField;
